/*
 * <sticky-atc> — mobile-only sticky add-to-cart bar for the product page.
 *
 * - Slides in once the main product form (.product-buy-buttons) has scrolled ABOVE the
 *   viewport; hides when it scrolls back into view. Does not show when the form is still
 *   below the fold.
 * - Clicking it smooth-scrolls back up to the form (it does NOT add to cart).
 * - Hides when the main add-to-cart button is disabled (selected variant / product sold out),
 *   read directly from the button's `disabled` attribute so all sold-out paths are covered.
 * - Mobile only (<=749px); inert on desktop. Re-initialises itself on combined-listing swaps
 *   via connected/disconnected callbacks.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(max-width: 749px)');
        this.onMediaChange = this.onMediaChange.bind(this);
        this.onTriggerIntersect = this.onTriggerIntersect.bind(this);
        this.onAvailabilityChange = this.onAvailabilityChange.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        this.button = this.querySelector('.bb-sticky-atc__button');
        this.submitButton =
          (this.productInfo || document).querySelector(`#ProductSubmitButton-${this.sectionId}`);
        this.trigger = (this.productInfo || document).querySelector('.product-buy-buttons');

        // Nothing to anchor to (e.g. block removed/reordered) — stay inert.
        if (!this.button || !this.submitButton || !this.trigger) return;

        this.button.addEventListener('click', this.onClick);
        this.mql.addEventListener('change', this.onMediaChange);

        if (this.mql.matches) this.activate();
      }

      disconnectedCallback() {
        this.deactivate();
        this.mql.removeEventListener('change', this.onMediaChange);
        if (this.button) this.button.removeEventListener('click', this.onClick);
      }

      onMediaChange(event) {
        if (event.matches) {
          this.activate();
        } else {
          this.deactivate();
        }
      }

      activate() {
        if (this.intersectionObserver) return;

        // Bottom rootMargin of -100% shrinks the observation root to a line at the very top
        // of the viewport, so the callback fires when the TOP of the form crosses it.
        this.intersectionObserver = new IntersectionObserver(this.onTriggerIntersect, {
          rootMargin: '0px 0px -100% 0px',
        });
        this.intersectionObserver.observe(this.trigger);

        this.availabilityObserver = new MutationObserver(this.onAvailabilityChange);
        this.availabilityObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ['disabled'],
        });
        this.syncAvailability();
      }

      deactivate() {
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        }
        if (this.availabilityObserver) {
          this.availabilityObserver.disconnect();
          this.availabilityObserver = null;
        }
        this.classList.remove('bb-sticky-atc--visible', 'bb-sticky-atc--unavailable');
      }

      // Show once the TOP of the form has scrolled above the viewport top (top < 0).
      // Stays hidden while the form is still below the fold (top >= 0).
      onTriggerIntersect(entries) {
        const entry = entries[0];
        const scrolledPast = entry.boundingClientRect.top < 0;
        this.classList.toggle('bb-sticky-atc--visible', scrolledPast);
      }

      onAvailabilityChange() {
        this.syncAvailability();
      }

      syncAvailability() {
        this.classList.toggle('bb-sticky-atc--unavailable', this.submitButton.disabled);
      }

      onClick() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const headerHeight = this.headerOffset();
        const top =
          this.trigger.getBoundingClientRect().top + window.scrollY - headerHeight - 16;

        window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });

        // Land keyboard/SR focus on the form rather than the now-offscreen sticky button.
        // NB: do NOT focus a <select> — calling .focus() on a <select> opens the
        // native picker on iOS Safari + Chrome Android, which we don't want here
        // (the user clicked "Add to Cart", not "open size picker"). For dropdown
        // pickers we fall through to the submit button.
        const focusTarget =
          (this.productInfo || document).querySelector(
            'variant-selects input:not([disabled])'
          ) || this.submitButton;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }

      headerOffset() {
        const fromVar = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
          10
        );
        if (!Number.isNaN(fromVar) && fromVar > 0) return fromVar;
        const header = document.querySelector('.header-wrapper') || document.querySelector('header');
        return header ? header.offsetHeight : 0;
      }
    }
  );
}
